import { useEffect, useRef, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { PlatformMapProps, MapCoordinate } from './PlatformMap.types';

// Leaflet types
declare global {
  interface Window {
    L: any;
  }
}

export const PlatformMap = ({
  initialRegion,
  markerPosition,
  onPress,
  style,
  interactive = true,
}: PlatformMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const [leafletLoaded, setLeafletLoaded] = useState(false);

  // Load Leaflet CSS and JS dynamically
  useEffect(() => {
    // Check if already loaded
    if (window.L) {
      setLeafletLoaded(true);
      return;
    }

    // Load CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
    link.crossOrigin = '';
    document.head.appendChild(link);

    // Load JS
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
    script.crossOrigin = '';
    script.onload = () => setLeafletLoaded(true);
    document.head.appendChild(script);

    return () => {
      // Cleanup not needed as we want Leaflet to persist
    };
  }, []);

  // Initialize map when Leaflet is loaded
  useEffect(() => {
    if (!leafletLoaded || !mapRef.current || mapInstanceRef.current) return;

    const L = window.L;
    const lat = initialRegion?.latitude || 40.7128;
    const lng = initialRegion?.longitude || -74.006;
    const zoom = 13;

    // Create map
    const map = L.map(mapRef.current, {
      center: [lat, lng],
      zoom,
      scrollWheelZoom: interactive,
      dragging: interactive,
      touchZoom: interactive,
      doubleClickZoom: interactive,
      boxZoom: interactive,
      keyboard: interactive,
    });

    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    // Add click handler
    if (onPress && interactive) {
      map.on('click', (e: any) => {
        const coordinate: MapCoordinate = {
          latitude: e.latlng.lat,
          longitude: e.latlng.lng,
        };
        onPress(coordinate);
      });
    }

    mapInstanceRef.current = map;

    // Cleanup
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [leafletLoaded, initialRegion, onPress, interactive]);

  // Update marker when position changes
  useEffect(() => {
    if (!leafletLoaded || !mapInstanceRef.current) return;

    const L = window.L;
    const map = mapInstanceRef.current;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }

    // Add new marker if position exists
    if (markerPosition) {
      markerRef.current = L.marker([markerPosition.latitude, markerPosition.longitude]).addTo(map);
    }
  }, [leafletLoaded, markerPosition]);

  // Get numeric dimensions from style
  const flatStyle = StyleSheet.flatten(style) || {};
  const height = typeof flatStyle.height === 'number' ? flatStyle.height : 250;
  const borderRadius = typeof flatStyle.borderRadius === 'number' ? flatStyle.borderRadius : 10;

  return (
    <View style={[style, { overflow: 'hidden' }]}>
      <div
        ref={mapRef}
        style={{
          width: '100%',
          height: height,
          borderRadius: borderRadius,
        }}
      />
    </View>
  );
};
